import React, { useState, useEffect } from 'react';
import { FiUsers, FiPlusCircle, FiCheckCircle, FiSearch, FiMapPin, FiXCircle, FiGrid, FiTrash2 } from 'react-icons/fi';
import api from '../../utils/api';

const font = "'Outfit', sans-serif";
const C = { text: '#0f172a', sub: '#64748b', border: '#bae6fd', bg: '#f0f9ff', white: '#fff', accent: '#0ea5e9' };

const SeatPlan = () => {
    const [sessions, setSessions] = useState([]);
    const [selectedSession, setSelectedSession] = useState('');
    const [sessionInfo, setSessionInfo] = useState(null);
    const [assignments, setAssignments] = useState([]);
    const [selectedAssignment, setSelectedAssignment] = useState(null);
    const [seats, setSeats] = useState([]);
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState('');
    const [search, setSearch] = useState('');
    const [addSeatCount, setAddSeatCount] = useState(1);
    const [assigningSeat, setAssigningSeat] = useState(null);
    const [sessionSearch, setSessionSearch] = useState('');
    const [seatSearch, setSeatSearch] = useState('');

    useEffect(() => {
        api.get('/api/exam-controller/sessions')
            .then(r => setSessions(r.data.sessions))
            .catch(() => { });
    }, []);

    // When session changes → load halls + auto-fetch students for that department/batch
    useEffect(() => {
        if (!selectedSession) {
            setAssignments([]); setSelectedAssignment(null); setSeats([]); setStudents([]); setSessionInfo(null);
            return;
        }
        const sess = sessions.find(s => String(s.id) === String(selectedSession));
        setSessionInfo(sess);

        // Load hall assignments
        api.get(`/api/exam-controller/sessions/${selectedSession}/assignments`)
            .then(r => { setAssignments(r.data.assignments); setSelectedAssignment(null); setSeats([]); })
            .catch(() => setAssignments([]));

        // Auto-fetch students from this session's department & batch
        if (sess) {
            setLoading(true);
            const dept = encodeURIComponent(sess.department || '');
            const batch = encodeURIComponent(sess.batch_name || '');
            api.get(`/api/exam-controller/students?department=${dept}&batch_name=${batch}`)
                .then(r => setStudents(r.data.students || []))
                .catch(() => setStudents([]))
                .finally(() => setLoading(false));
        }
    }, [selectedSession]); // eslint-disable-line

    const [allSessionSeats, setAllSessionSeats] = useState([]);

    const [isLoaded, setIsLoaded] = useState(false);

    // Load seats when hall assignment selected
    useEffect(() => {
        setIsLoaded(false);
        if (selectedAssignment) loadSeats();
        else { setSeats([]); setAllSessionSeats([]); }
    }, [selectedAssignment]); // eslint-disable-line

    const loadSeats = async () => {
        if (!selectedAssignment || !selectedSession) return;
        setLoading(true);
        try {
            const r = await api.get(`/api/exam-controller/sessions/${selectedSession}/seat-plan`);
            const all = r.data.seats || [];
            setAllSessionSeats(all);
            const hallSeats = all.filter(s => s.assignment_id === selectedAssignment.id);
            setSeats(hallSeats);
            
            // AUTO-INIT check: If hall is empty (0 seats in DB), auto-populate capacity
            if (hallSeats.length === 0 && selectedAssignment.capacity > 0) {
                console.log(`[AUTO-INIT] Hall ${selectedAssignment.hall_number} is empty. Adding ${selectedAssignment.capacity} seats...`);
                await addSeats(selectedAssignment.capacity);
            }
        } catch { setSeats([]); setAllSessionSeats([]); }
        finally { setLoading(false); setIsLoaded(true); }
    };

    // Add empty seats to the hall
    const addSeats = async (count = addSeatCount) => {
        if (!selectedAssignment || count < 1) return;
        setMsg('');
        
        // Find max hall seat (Standard)
        // Refresh allSessionSeats from a local source if possible to avoid state lag
        const hallSeats = allSessionSeats.filter(s => s.assignment_id === selectedAssignment.id);
        const maxHallSeat = hallSeats.length > 0 
            ? Math.max(...hallSeats.map(s => s.seat_number)) 
            : 0;

        const newSeats = [];
        for (let i = 1; i <= count; i++) {
            newSeats.push({
                seat_number: maxHallSeat + i,
                student_username: null,
                student_name: null,
                roll_number: null
            });
        }
        try {
            await api.post(`/api/exam-controller/sessions/${selectedSession}/allocate-seats`, {
                assignment_id: selectedAssignment.id,
                students: newSeats
            });
            // We don't call loadSeats() here because loadSeats() itself calls addSeats() for the first-time init.
            // If it's a manual add, we do need it:
            if (!loading) loadSeats();
        } catch (err) { setMsg('❌ ' + (err.response?.data?.message || 'Failed to add seats')); }
    };

    // Assign student to a specific seat
    const assignStudentToSeat = async (student) => {
        if (!assigningSeat || !selectedSession) return;
        setMsg('');
        try {
            await api.post(`/api/exam-controller/sessions/${selectedSession}/allocate-seats`, {
                assignment_id: selectedAssignment.id,
                students: [{
                    seat_number: assigningSeat.seat_number,
                    student_username: student.username,
                    student_name: student.name,
                    roll_number: student.roll_number
                }]
            });
            setMsg(`✅ ${student.name} → Seat ${assigningSeat.seat_number}`);
            setAssigningSeat(null);
            loadSeats();
        } catch (err) { setMsg('❌ ' + (err.response?.data?.message || 'Failed to assign')); }
    };

    // Clear a seat
    const removeSeatAllocation = async (seat) => {
        if (!window.confirm(`Clear Seat ${seat.seat_number}?`)) return;
        try {
            await api.post(`/api/exam-controller/sessions/${selectedSession}/allocate-seats`, {
                assignment_id: selectedAssignment.id,
                students: [{ seat_number: seat.seat_number, student_username: null, student_name: null, roll_number: null }]
            });
            setMsg(`Seat ${seat.seat_number} cleared`);
            loadSeats();
        } catch { setMsg('Failed to clear seat'); }
    };

    // Delete a seat completely
    const deleteSeat = async (seat) => {
        if (!window.confirm(`Delete Seat ${seat.seat_number} entirely? This cannot be undone.`)) return;
        try {
            await api.delete(`/api/exam-controller/seats/${seat.id}`);
            setMsg(`✅ Seat ${seat.seat_number} deleted`);
            setAssigningSeat(null);
            loadSeats();
        } catch (err) {
            setMsg('❌ ' + (err.response?.data?.message || 'Failed to delete seat'));
        }
    };
    
    const autoShuffle = async () => {
        if (!selectedAssignment || !selectedSession) return;
        const unassignedStudents = students.filter(s => !allSessionSeats.some(x => x.student_username === s.username));
        const emptySeats = seats.filter(s => !s.student_username);
        
        if (unassignedStudents.length === 0) { setMsg('ℹ️ No unassigned students left!'); return; }
        if (emptySeats.length === 0) { setMsg('ℹ️ No empty seats in this hall!'); return; }
        if (!window.confirm(`Auto-assign ${Math.min(unassignedStudents.length, emptySeats.length)} students to empty seats randomly?`)) return;

        const count = Math.min(unassignedStudents.length, emptySeats.length);
        const shuffledStudents = [...unassignedStudents].sort(() => Math.random() - 0.5);
        
        const allocations = [];
        for (let i = 0; i < count; i++) {
            allocations.push({
                seat_number: emptySeats[i].seat_number,
                student_username: shuffledStudents[i].username,
                student_name: shuffledStudents[i].name,
                roll_number: shuffledStudents[i].roll_number
            });
        }

        setLoading(true);
        try {
            await api.post(`/api/exam-controller/sessions/${selectedSession}/allocate-seats`, {
                assignment_id: selectedAssignment.id,
                students: allocations
            });
            setMsg(`✅ Randomly assigned ${count} students!`);
            loadSeats();
        } catch (err) {
            setMsg('❌ Failed auto-assignment');
        } finally {
            setLoading(false);
        }
    };

    const unassignAll = async () => {
        if (!selectedAssignment || !selectedSession) return;
        const assignedSeats = seats.filter(s => s.student_username);
        
        if (assignedSeats.length === 0) { setMsg('ℹ️ No assigned seats to clear!'); return; }
        if (!window.confirm(`Clear all ${assignedSeats.length} student assignments in this hall?`)) return;

        const allocations = assignedSeats.map(s => ({
            seat_number: s.seat_number,
            student_username: null,
            student_name: null,
            roll_number: null
        }));

        setLoading(true);
        try {
            await api.post(`/api/exam-controller/sessions/${selectedSession}/allocate-seats`, {
                assignment_id: selectedAssignment.id,
                students: allocations
            });
            setMsg(`✅ Cleared ${assignedSeats.length} assignments!`);
            loadSeats();
        } catch (err) {
            setMsg('❌ Failed to unassign all');
        } finally {
            setLoading(false);
        }
    };

    const filteredStudents = students.filter(s =>
        (s.name || '').toLowerCase().includes(search.toLowerCase()) ||
        (s.roll_number || '').toLowerCase().includes(search.toLowerCase()) ||
        (s.username || '').toLowerCase().includes(search.toLowerCase())
    );

    const filteredSeats = seats.filter(s => {
        const term = seatSearch.toLowerCase();
        if (!term) return true;
        return (s.student_name || '').toLowerCase().includes(term) ||
               (s.student_username || '').toLowerCase().includes(term) ||
               (s.roll_number || '').toLowerCase().includes(term) ||
               s.seat_number.toString().includes(term);
    });

    const occupiedSeats = seats.filter(s => s.student_username && s.student_username !== '');

    return (
        <div style={{ fontFamily: font }}>
            <h1 style={{ fontSize: 24, fontWeight: 900, color: C.text, margin: '0 0 8px' }}>Seat Plan Management</h1>
            <p style={{ color: C.sub, fontSize: 14, margin: '0 0 24px' }}>Add seats to halls, then assign students to each seat. Students are auto-loaded from the exam's department & batch.</p>

            {msg && <div style={{ padding: '10px 14px', background: msg.startsWith('✅') ? '#d1fae5' : msg.startsWith('❌') ? '#fee2e2' : '#fef3c7', color: msg.startsWith('✅') ? '#065f46' : msg.startsWith('❌') ? '#991b1b' : '#92400e', borderRadius: 12, marginBottom: 20, fontWeight: 700, fontSize: 13 }}>{msg}</div>}

            {/* Step 1: Select Session */}
            <div style={{ background: C.white, borderRadius: 16, border: `1.5px solid ${C.border}`, padding: 20, marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: C.accent, textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 8 }}>Step 1 — Select Exam Session</label>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    <div style={{ position: 'relative', flex: 1 }}>
                        <input 
                            type="text" 
                            placeholder="🔍 Search session by subject, department or date..." 
                            value={sessionSearch}
                            onChange={(e) => setSessionSearch(e.target.value)}
                            style={{ width: '100%', padding: '9px 13px', borderRadius: 10, border: `1.5px solid ${C.border}`, fontSize: 13, fontFamily: font, outline: 'none', boxSizing: 'border-box', background: '#fff' }}
                        />
                        {sessionSearch && (
                            <button 
                                type="button" 
                                onClick={() => setSessionSearch('')} 
                                style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: C.sub, cursor: 'pointer', fontSize: 14, fontWeight: 800 }}
                            >
                                ×
                            </button>
                        )}
                    </div>
                </div>
                <select value={selectedSession} onChange={e => setSelectedSession(e.target.value)} style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: `1.5px solid ${C.border}`, fontSize: 13, fontFamily: font, outline: 'none', background: C.bg }}>
                    <option value="">— Select a session —</option>
                    {sessions
                        .filter(s => {
                            const term = sessionSearch.toLowerCase();
                            return !sessionSearch || 
                                s.subject_name?.toLowerCase().includes(term) || 
                                s.department?.toLowerCase().includes(term) || 
                                s.exam_date?.includes(term);
                        })
                        .map(s => <option key={s.id} value={s.id}>{s.subject_name} | {s.department} Sem {s.semester} | {s.exam_date?.slice(0, 10)}</option>)
                    }
                </select>
                {sessionSearch && sessions.filter(s => {
                    const term = sessionSearch.toLowerCase();
                    return s.subject_name?.toLowerCase().includes(term) || 
                           s.department?.toLowerCase().includes(term) || 
                           s.exam_date?.includes(term);
                }).length === 0 && (
                    <div style={{ fontSize: 11, color: '#ef4444', marginTop: 4, fontWeight: 600 }}>No matching sessions found.</div>
                )}
                {sessionInfo && (
                    <div style={{ marginTop: 10, padding: '8px 14px', background: '#e0f2fe', borderRadius: 10, fontSize: 12, color: C.text }}>
                        📋 <strong>{sessionInfo.subject_name}</strong> — {sessionInfo.department} • Batch: {sessionInfo.batch_name} • {sessionInfo.exam_date?.slice(0, 10)}
                        <span style={{ marginLeft: 10, color: C.sub }}>({students.length} students auto-loaded)</span>
                    </div>
                )}
            </div>

            {/* Step 2: Select Hall */}
            {selectedSession && (
                <div style={{ background: C.white, borderRadius: 16, border: `1.5px solid ${C.border}`, padding: 20, marginBottom: 20 }}>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: C.accent, textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 8 }}>Step 2 — Select Hall</label>
                    {assignments.length === 0 ? (
                        <p style={{ color: C.sub, fontSize: 13, margin: 0 }}>No halls assigned. Go to <strong>Assign Halls</strong> first.</p>
                    ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                            {assignments.map(a => (
                                <button key={a.id} onClick={() => { setSelectedAssignment(a); setAssigningSeat(null); }} style={{
                                    padding: '12px 18px', borderRadius: 14,
                                    border: `2px solid ${selectedAssignment?.id === a.id ? C.accent : C.border}`,
                                    background: selectedAssignment?.id === a.id ? '#e0f2fe' : C.bg,
                                    color: selectedAssignment?.id === a.id ? C.accent : C.text,
                                    fontWeight: 800, fontSize: 13, cursor: 'pointer', fontFamily: font,
                                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minWidth: 120
                                }}>
                                    <FiMapPin size={16} />
                                    <span>{a.hall_number}</span>
                                    <span style={{ fontSize: 10, fontWeight: 600, color: C.sub }}>{a.building} • {a.capacity} cap</span>
                                    <span style={{ fontSize: 10, fontWeight: 600, color: C.sub }}>Inv: {a.invigilator_username}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Step 3: Seats + Students (Side by Side) */}
            {selectedAssignment && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

                    {/* LEFT: Seat Grid */}
                    <div style={{ background: C.white, borderRadius: 20, border: `1.5px solid ${C.border}`, overflow: 'hidden' }}>
                        <div style={{ padding: '14px 20px', background: C.bg, borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontWeight: 800, fontSize: 14, color: C.text }}>🪑 Seats — {selectedAssignment.hall_number}</span>
                            <span style={{ fontSize: 12, color: C.accent, fontWeight: 700 }}>{occupiedSeats.length}/{seats.length} filled</span>
                        </div>

                        {/* Add Seats & Search */}
                        <div style={{ padding: '12px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', gap: 12, background: '#fafffe' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <FiGrid size={15} color={C.accent} />
                                    <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>Rows:</span>
                                    <input type="number" min="1" max="200" value={addSeatCount} onChange={e => setAddSeatCount(parseInt(e.target.value) || 1)} style={{ width: 50, padding: '6px 8px', borderRadius: 8, border: `1.5px solid ${C.border}`, fontSize: 13, fontFamily: font, outline: 'none', textAlign: 'center' }} />
                                    <button onClick={() => addSeats()} style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#38bdf8,#0ea5e9)', color: '#fff', fontWeight: 800, fontSize: 11, cursor: 'pointer', fontFamily: font, display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <FiPlusCircle size={13} /> Add Seats
                                    </button>
                                </div>
                                <div style={{ height: 24, width: 1, background: C.border }}></div>
                                <button 
                                    onClick={autoShuffle} 
                                    disabled={loading}
                                    style={{ padding: '7px 14px', borderRadius: 8, border: `1.5px solid #10b981`, background: '#ecfdf5', color: '#059669', fontWeight: 800, fontSize: 11, cursor: 'pointer', fontFamily: font, display: 'flex', alignItems: 'center', gap: 6 }}
                                >
                                    🎲 Auto-Shuffle
                                </button>
                                <button 
                                    onClick={unassignAll} 
                                    disabled={loading}
                                    style={{ padding: '7px 14px', borderRadius: 8, border: `1.5px solid #ef4444`, background: '#fef2f2', color: '#dc2626', fontWeight: 800, fontSize: 11, cursor: 'pointer', fontFamily: font, display: 'flex', alignItems: 'center', gap: 6 }}
                                >
                                    🗑️ Unassign All
                                </button>
                            </div>
                            <div style={{ position: 'relative' }}>
                                <input 
                                    type="text" 
                                    placeholder="🔍 Search student in seats..." 
                                    value={seatSearch}
                                    onChange={(e) => setSeatSearch(e.target.value)}
                                    style={{ width: '100%', padding: '8px 12px', paddingLeft: 36, borderRadius: 10, border: `1.5px solid ${C.border}`, fontSize: 12, fontFamily: font, outline: 'none', background: '#fff' }}
                                />
                                <FiSearch size={14} color={C.sub} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
                                {seatSearch && (
                                    <button onClick={() => setSeatSearch('')} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: C.sub, cursor: 'pointer', fontSize: 14, fontWeight: 800 }}>×</button>
                                )}
                            </div>
                        </div>

                            <div style={{ maxHeight: 500, overflowY: 'auto' }}>
                                {filteredSeats.length === 0 ? (
                                    <div style={{ padding: 30, textAlign: 'center', color: C.sub, fontSize: 13 }}>
                                        {seats.length === 0 ? 'No seats yet.' : 'No matching seats found.'}
                                    </div>
                                ) : (
                                    filteredSeats.map(s => {
                                    const hasStudent = s.student_username && s.student_username !== '';
                                    const isAssigning = assigningSeat?.seat_number === s.seat_number;
                                    return (
                                        <div key={s.id || s.seat_number} style={{
                                            padding: '8px 20px', borderBottom: `1px solid ${C.border}`,
                                            display: 'flex', alignItems: 'center', gap: 10,
                                            background: isAssigning ? '#dbeafe' : hasStudent ? '#f0fdf4' : '#fff'
                                        }}>
                                            <div style={{
                                                width: 32, height: 32, borderRadius: 8,
                                                background: hasStudent ? '#bbf7d0' : isAssigning ? '#93c5fd' : '#f1f5f9',
                                                color: hasStudent ? '#16a34a' : isAssigning ? '#1d4ed8' : C.sub,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                fontWeight: 900, fontSize: 12, flexShrink: 0
                                            }}>
                                                {s.seat_number}
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                {hasStudent ? (
                                                    <>
                                                        <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{s.student_name}</div>
                                                        <div style={{ fontSize: 10, color: C.sub }}>{s.roll_number} • <span style={{ color: '#0369a1' }}>{s.anonymous_id}</span></div>
                                                    </>
                                                ) : (
                                                    <span style={{ fontSize: 11, color: C.sub, fontStyle: 'italic' }}>Empty</span>
                                                )}
                                            </div>
                                            <div style={{ display: 'flex', gap: 6 }}>
                                                {!hasStudent && (
                                                    <button onClick={() => setAssigningSeat(isAssigning ? null : s)} style={{
                                                        padding: '4px 10px', borderRadius: 6, border: 'none',
                                                        background: isAssigning ? '#fecaca' : '#dbeafe',
                                                        color: isAssigning ? '#dc2626' : '#2563eb',
                                                        fontWeight: 700, fontSize: 10, cursor: 'pointer', fontFamily: font
                                                    }}>
                                                        {isAssigning ? '✕ Cancel' : '+ Assign'}
                                                    </button>
                                                )}
                                                {hasStudent && (
                                                    <button onClick={() => removeSeatAllocation(s)} title="Clear student from seat" style={{
                                                        width: 26, height: 26, borderRadius: 6, border: 'none',
                                                        background: '#fee2e2', color: '#dc2626',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer'
                                                    }}>
                                                        <FiXCircle size={12} />
                                                    </button>
                                                )}
                                                <button onClick={() => deleteSeat(s)} title="Delete seat row entirely" style={{
                                                    width: 26, height: 26, borderRadius: 6, border: 'none',
                                                    background: '#fee2e2', color: '#dc2626',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer'
                                                }}>
                                                    <FiTrash2 size={12} />
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>

                    {/* RIGHT: Students (auto-loaded) */}
                    <div style={{ background: C.white, borderRadius: 20, border: `1.5px solid ${assigningSeat ? C.accent : C.border}`, overflow: 'hidden', transition: 'border-color 0.2s' }}>
                        <div style={{
                            padding: '14px 20px',
                            background: assigningSeat ? '#dbeafe' : C.bg,
                            borderBottom: `1px solid ${C.border}`,
                            fontWeight: 800, fontSize: 14, color: C.text,
                            transition: 'background 0.2s'
                        }}>
                            {assigningSeat
                                ? `🎯 Click a student to assign → Seat ${assigningSeat.seat_number}`
                                : `👩‍🎓 Students — ${sessionInfo?.department || ''} • ${sessionInfo?.batch_name || ''}`
                            }
                            <span style={{ float: 'right', fontSize: 12, color: C.accent, fontWeight: 700 }}>{students.length} students</span>
                        </div>

                        {/* Filter */}
                        <div style={{ padding: '10px 16px', borderBottom: `1px solid ${C.border}` }}>
                            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍  Filter by name, roll number or username..." style={{ width: '100%', padding: '8px 12px', borderRadius: 10, border: `1.5px solid ${C.border}`, fontSize: 12, fontFamily: font, outline: 'none', boxSizing: 'border-box' }} />
                        </div>

                        <div style={{ maxHeight: 480, overflowY: 'auto' }}>
                            {loading ? (
                                <div style={{ padding: 30, textAlign: 'center', color: C.sub, fontSize: 13 }}>Loading students...</div>
                            ) : filteredStudents.length > 0 ? filteredStudents.map(s => {
                                const seated = allSessionSeats.find(x => x.student_username === s.username && x.student_username);
                                const isThisHall = seated && seated.assignment_id === selectedAssignment.id;
                                return (
                                    <div key={s.username || s.student_id}
                                        onClick={() => assigningSeat && !seated && assignStudentToSeat(s)}
                                        style={{
                                            padding: '8px 16px', borderBottom: `1px solid ${C.border}`,
                                            display: 'flex', alignItems: 'center', gap: 10,
                                            background: seated ? (isThisHall ? '#f0fdf4' : '#fffbeb') : assigningSeat ? '#fafeff' : '#fff',
                                            cursor: assigningSeat && !seated ? 'pointer' : 'default',
                                            opacity: seated ? 0.6 : 1,
                                            transition: 'background 0.1s'
                                        }}
                                        onMouseEnter={e => { if (assigningSeat && !seated) e.currentTarget.style.background = '#dbeafe'; }}
                                        onMouseLeave={e => { if (assigningSeat && !seated) e.currentTarget.style.background = '#fafeff'; }}
                                    >
                                        <div style={{
                                            width: 30, height: 30, borderRadius: 8,
                                            background: seated ? (isThisHall ? '#bbf7d0' : '#fef3c7') : '#e0f2fe',
                                            color: seated ? (isThisHall ? '#16a34a' : '#d97706') : C.accent,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                                        }}>
                                            {seated ? <FiCheckCircle size={13} /> : <FiUsers size={13} />}
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{s.name}</div>
                                            <div style={{ fontSize: 10, color: C.sub }}>{s.roll_number} • {s.username}</div>
                                        </div>
                                        {seated && (
                                            <span style={{ fontSize: 10, fontWeight: 800, color: isThisHall ? '#16a34a' : '#d97706' }}>
                                                {isThisHall ? `Seat ${seated.seat_number} ✓` : `Seat ${seated.seat_number} in Hall ${seated.hall_number}`}
                                            </span>
                                        )}
                                        {assigningSeat && !seated && <span style={{ fontSize: 10, fontWeight: 700, color: '#2563eb' }}>Click to assign →</span>}
                                    </div>
                                );
                            }) : (
                                <div style={{ padding: 30, textAlign: 'center', color: C.sub, fontSize: 13 }}>
                                    {students.length === 0 ? 'No students found for this department/batch.' : 'No matching students.'}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SeatPlan;
